import type {
    PickerChevron,
    PickerEmptyBehavior,
    PickerFlyoutAutoOpen,
    PickerFlyoutQueryScope,
    PickerItemData,
    PickerNested,
    PickerParentSelection,
    PickerSectionData,
    PickerSelectionSummary,
    PickerStatus,
    PickerTriggerPreset,
} from "@design-system/Picker/Picker.types";
import type { ButtonProps } from "@design-system/Button/Button.types";
import type { PickerEntityItemEntity } from "@components/entity/PickerEntityItem";

/**
 * Sample data for the Picker sandbox and matrix. Values are generated per
 * page load so nothing here can be mistaken for a real record.
 */

/** Unique value for a sandbox row. */
export const makeValue = (label: string) => `${label}-${crypto.randomUUID()}`;

/** A sandbox row from its label and decorations. */
export const makeItem = ({
    label,
    description,
    rightText,
    disabled,
    disabledReason,
    destructive,
}: Omit<PickerItemData<string>, "value">): PickerItemData<string> => ({
    value: makeValue(label),
    label,
    description,
    rightText,
    disabled,
    disabledReason,
    destructive,
});

export const pickerPlaygroundStatuses: readonly PickerStatus[] = [
    "idle",
    "loading",
    "error",
];
/** The stage's trigger axis; `attached` needs a host box, so it lives in the usage examples. */
export type PickerPlaygroundTrigger =
    | "button"
    | Exclude<PickerTriggerPreset, "attached">;

export const pickerPlaygroundTriggers: readonly PickerPlaygroundTrigger[] = [
    "button",
    "cell",
    "input",
    "cellInput",
];

/** Text presets: the field is the search. */
export const isPickerPlaygroundTextTrigger = (
    trigger: PickerPlaygroundTrigger,
): trigger is "input" | "cellInput" =>
    trigger === "input" || trigger === "cellInput";

/** Button variants the `button` stage can render; `primary` is the omitted-trigger default. */
export type PickerPlaygroundButtonVariant = ButtonProps["variant"];

export const pickerPlaygroundButtonVariants: readonly PickerPlaygroundButtonVariant[] =
    ["primary", "secondary", "tertiary", "ghost", "link", "accent"];
export const pickerPlaygroundSummaryModes: ReadonlyArray<
    PickerSelectionSummary["mode"]
> = ["mixed", "list", "count"];
export const pickerPlaygroundEmptyBehaviors: readonly PickerEmptyBehavior[] = [
    "show",
    "hide",
];
export const pickerPlaygroundParentSelections: readonly PickerParentSelection[] =
    ["self", "cascade"];

/** Which flyout levels get a search or footer: none, every level, the first level or the deepest ones. */
export type PickerPlaygroundFlyoutScope = "none" | "all" | "first" | "deepest";

export const pickerPlaygroundFlyoutScopes: readonly PickerPlaygroundFlyoutScope[] =
    ["none", "all", "first", "deepest"];

export const pickerPlaygroundFlyoutQueryScopes: readonly PickerFlyoutQueryScope[] =
    ["level", "path"];
export const pickerPlaygroundFlyoutAutoOpens: readonly PickerFlyoutAutoOpen[] =
    ["none", "path"];

export const pickerPlaygroundNested: readonly PickerNested[] = [
    "tree",
    "flyout",
];
export const pickerPlaygroundChevrons: readonly PickerChevron[] = [
    "always",
    "hover",
    "none",
];

export interface PickerPlaygroundFeatures {
    status: PickerStatus;
    trigger: PickerPlaygroundTrigger;
    buttonVariant: PickerPlaygroundButtonVariant;
    summaryMode: PickerSelectionSummary["mode"];
    emptyBehavior: PickerEmptyBehavior;
    nested: PickerNested;
    parentSelection: PickerParentSelection;
    flyoutSearch: PickerPlaygroundFlyoutScope;
    flyoutFooter: PickerPlaygroundFlyoutScope;
    flyoutQueryScope: PickerFlyoutQueryScope;
    flyoutAutoOpen: PickerFlyoutAutoOpen;
    /** `undefined` keeps each preset's own default. */
    chevron: PickerChevron | undefined;
    isMultiple: boolean;
    isSearchable: boolean;
    /** Text triggers: whether typing filters the rows. */
    isFilterable: boolean;
    /** Text triggers: whether the typed text is kept as the value. */
    allowsCustomValue: boolean;
    hasSections: boolean;
    hasFooter: boolean;
    isTree: boolean;
    /** Tree / flyout parents hold a value of their own; follows `isMultiple` until toggled. */
    hasSelectableParents: boolean;
    /** Inline tree: every parent starts expanded (`defaultExpandAll`). */
    isExpandedByDefault: boolean;
    isEmpty: boolean;
    hasDisabledItems: boolean;
    hasDestructiveItem: boolean;
    hasDescriptions: boolean;
    hasRightText: boolean;
    isDisabled: boolean;
    isClearable: boolean;
    /** Rows come from a fake server: pages of 20, server search, load more. */
    isPaged: boolean;
    /** Paged only: next-page requests fail; turn it off, then press "Try again". */
    isNextPageFailing: boolean;
}

export const defaultPickerFeatures: PickerPlaygroundFeatures = {
    status: "idle",
    trigger: "button",
    buttonVariant: "primary",
    summaryMode: "mixed",
    emptyBehavior: "show",
    nested: "tree",
    parentSelection: "self",
    flyoutSearch: "none",
    flyoutFooter: "none",
    flyoutQueryScope: "level",
    flyoutAutoOpen: "none",
    chevron: undefined,
    isMultiple: false,
    isSearchable: false,
    isFilterable: true,
    allowsCustomValue: false,
    hasSections: false,
    hasFooter: false,
    isTree: false,
    hasSelectableParents: false,
    isExpandedByDefault: false,
    isEmpty: false,
    hasDisabledItems: false,
    hasDestructiveItem: false,
    hasDescriptions: false,
    hasRightText: false,
    isDisabled: false,
    isClearable: false,
    isPaged: false,
    isNextPageFailing: false,
};

const flatLabels = [
    "Operating account",
    "Payroll account",
    "Savings",
    "Corporate card",
    "Petty cash",
];

const descriptions: Record<string, string> = {
    "Operating account": "Day-to-day spending and deposits",
    "Payroll account": "Funded before every pay run",
    Savings: "Reserve for quarterly taxes",
    "Corporate card": "Shared team card",
    "Petty cash": "Office drawer",
};

const rightTexts: Record<string, string> = {
    "Operating account": "1,204 transactions",
    "Payroll account": "88 transactions",
    Savings: "12 transactions",
    "Corporate card": "410 transactions",
    "Petty cash": "5 transactions",
};

/** Flat rows, decorated per the sandbox toggles. */
export const buildFlatItems = ({
    hasDisabledItems,
    hasDestructiveItem,
    hasDescriptions,
    hasRightText,
}: Pick<
    PickerPlaygroundFeatures,
    | "hasDisabledItems"
    | "hasDestructiveItem"
    | "hasDescriptions"
    | "hasRightText"
>): Array<PickerItemData<string>> => {
    const items = flatLabels.map((label, index) =>
        makeItem({
            label,
            description: hasDescriptions ? descriptions[label] : undefined,
            rightText: hasRightText ? rightTexts[label] : undefined,
            disabled: hasDisabledItems && index === 2,
            disabledReason:
                hasDisabledItems && index === 2
                    ? "Savings is locked for the current period"
                    : undefined,
        }),
    );

    if (hasDestructiveItem) {
        items.push(makeItem({ label: "Archive account", destructive: true }));
    }

    return items;
};

/** The undecorated flat list every matrix and example starts from. */
export const plainFlatItemOptions: Parameters<typeof buildFlatItems>[0] = {
    hasDisabledItems: false,
    hasDestructiveItem: false,
    hasDescriptions: false,
    hasRightText: false,
};

/** Two sections: bank accounts and cards. */
export const buildSections = (
    options: Parameters<typeof buildFlatItems>[0],
): Array<{ title: string; items: Array<PickerItemData<string>> }> => {
    const items = buildFlatItems(options);

    return [
        { title: "Bank accounts", items: items.slice(0, 3) },
        { title: "Cards and cash", items: items.slice(3) },
    ];
};

/** Data-driven sections with select-all, for the roots' `sections` prop. */
export const buildDataSections = (
    options: Parameters<typeof buildFlatItems>[0],
): Array<PickerSectionData<string>> =>
    buildSections(options).map((section) => ({
        title: section.title,
        items: section.items,
        selectAll: true,
    }));

const entityColors = ["#DCE8FF", "#FDE2E2", "#DCF4EA", "#FCECC9"];

/** Entities shaped for `PickerEntityItem`. */
export const buildEntities = (): Array<
    PickerEntityItemEntity & { id: number }
> =>
    [
        "Northwind Traders",
        "Acme Holdings",
        "Bluebird Studio",
        "Canyon Creative",
    ].map((name, index) => ({
        id: index + 1,
        name,
        iconColor: entityColors[index] ?? entityColors[0],
        logo: null,
    }));

/** Free-text suggestions for `Picker.Select allowCustomValue`. */
export const buildSuggestions = (): Array<PickerItemData<string>> =>
    ["Coffee", "Consulting", "Contractors", "Rent", "Software"].map((label) =>
        makeItem({ label }),
    );

/** Accounts shaped for `Picker.AccountItem`. */
export const buildAccounts = (): Array<{
    value: string;
    name: string;
    mask: string;
}> =>
    [
        ["Business checking", "2490"],
        ["Business savings", "8812"],
        ["Platinum card", "0031"],
    ].map(([name, mask]) => ({ value: makeValue(name), name, mask }));
